import React, { useState, useEffect } from 'react';
import { IconClose } from './Icons';
import { track } from '../analytics';
import { pricingVariant, FLAT_MONTHLY_USD } from '../pricingExperiment';
import './PlanPanel.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || '';

// Sales-led (Enterprise) contact address — mirrors the storefront's "Talk to
// us" mailto so the app and marketing send the same signal.
const ENTERPRISE_CONTACT = 'mailto:founder@symphonate.ai?subject=Symphonate%20Enterprise';

function authHeaders() {
  const token = localStorage.getItem('symphonate-token');
  return { Authorization: `Bearer ${token}` };
}

// Whether this plan is shown as a flat per-workspace price in the given arm.
// Sales-led tiers (Enterprise) are never flat-priced — they're contact-us and
// carry no entry in FLAT_MONTHLY_USD, so this is false for them either way.
function isFlatPriced(plan, variant) {
  return variant === 'flat' && FLAT_MONTHLY_USD[plan.id] !== undefined;
}

function priceLabel(plan, variant) {
  if (plan.salesLed) return 'Contact';
  if (plan.priceCentsPerSeat === 0) return 'Free';
  if (isFlatPriced(plan, variant)) return `$${FLAT_MONTHLY_USD[plan.id]}/mo flat`;
  return `$${Math.round(plan.priceCentsPerSeat / 100)}/seat/mo`;
}

function depthLabel(plan, variant) {
  const history =
    plan.historyRetentionDays === null
      ? 'Unlimited decision history'
      : `${plan.historyRetentionDays}-day AI recall window`;
  const integrations =
    plan.maxIntegrations === null
      ? 'Unlimited integrations'
      : `${plan.maxIntegrations} integration${plan.maxIntegrations === 1 ? '' : 's'}`;
  // Sales-led tiers are tailored (no fixed seat cap shown). Otherwise the flat
  // arm's pitch is unlimited seats for one price; the per-seat arm keeps the
  // seat cap. (Seat-cap enforcement is unchanged server-side — this is
  // presentation only, and nothing is charged in the fake door.)
  const seats = plan.salesLed
    ? 'Tailored to your org'
    : isFlatPriced(plan, variant)
      ? 'Unlimited seats'
      : `Up to ${plan.seatCap} seats`;
  return [history, integrations, seats];
}

/**
 * Plan & upgrade — the fake-door willingness-to-pay surface
 * (gtm-playbook.md §6 / §7.6). Paid tiers aren't purchasable yet; the
 * Upgrade buttons record intent (PostHog + a durable audit event via
 * POST .../billing/upgrade-intent) and confirm the early-supporter list.
 * No checkout happens here on purpose — the click is the data.
 *
 * Team runs the per-seat-vs-flat pricing A/B (pricingExperiment.js). Enterprise
 * is sales-led (`salesLed` on the billing catalog): no self-serve price, a
 * "Talk to us" contact CTA — matching the marketing storefront.
 */
export default function PlanPanel({ workspaceId, workspaceName, onClose }) {
  const [planInfo, setPlanInfo] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intent, setIntent] = useState({}); // planTier -> 'sent'

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/plan`, { headers: authHeaders() }).then(
        (r) => (r.ok ? r.json() : null),
      ),
      fetch(`${SERVER_URL}/api/plans`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([info, plans]) => {
        if (cancelled) return;
        if (!info || !plans) {
          setError('Could not load plan details.');
        } else {
          setPlanInfo(info);
          setCatalog(plans.plans || []);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load plan details.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // Fake-door pricing A/B arm for this workspace (per_seat | flat). Deterministic
  // per workspace so the whole team sees one arm and the funnel splits cleanly.
  const variant = pricingVariant(workspaceId);

  const handleUpgradeIntent = async (planTier) => {
    if (intent[planTier]) return;
    setIntent((prev) => ({ ...prev, [planTier]: 'sent' }));
    track('upgrade-intent', { planTier, workspaceId, pricingVariant: variant });
    try {
      await fetch(`${SERVER_URL}/api/workspaces/${workspaceId}/billing/upgrade-intent`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ planTier, pricingVariant: variant }),
      });
    } catch {
      // Intent is best-effort — the PostHog event already fired and the
      // user-facing confirmation shouldn't depend on the network.
    }
  };

  const currentTier = planInfo?.workspace?.planTier || 'free';
  const seats = planInfo?.seats;
  const paidTiers = catalog.filter((p) => p.requiresPayment);

  return (
    <div className="plan-backdrop" onClick={onClose}>
      <div
        className="plan-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Plan and upgrade"
      >
        <div className="plan-header">
          <div>
            <h2>Plan &amp; upgrade</h2>
            <p className="plan-subtitle">{workspaceName}</p>
          </div>
          <button className="plan-close" onClick={onClose} aria-label="Close">
            <IconClose size={16} />
          </button>
        </div>

        {loading && <p className="plan-status">Loading…</p>}
        {error && <p className="plan-status plan-error">{error}</p>}

        {!loading && !error && planInfo && (
          <div className="plan-body">
            <div className="plan-current">
              <span className="plan-current-tier">{planInfo.plan?.name || currentTier}</span>
              <span className="plan-current-detail">
                {seats ? `${seats.used} of ${seats.cap} seats used` : ''}
              </span>
              <ul className="plan-depth-list">
                {planInfo.plan &&
                  depthLabel(planInfo.plan, variant).map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>

            <div className="plan-tiers">
              {paidTiers.map((p) => (
                <div className="plan-tier-card" key={p.id}>
                  <div className="plan-tier-head">
                    <span className="plan-tier-name">{p.name}</span>
                    <span className="plan-tier-price">{priceLabel(p, variant)}</span>
                  </div>
                  <p className="plan-tier-desc">{p.description}</p>
                  <ul className="plan-depth-list">
                    {depthLabel(p, variant).map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  {p.salesLed ? (
                    <a className="plan-upgrade-btn plan-contact-btn" href={ENTERPRISE_CONTACT}>
                      Talk to us
                    </a>
                  ) : currentTier === p.id ? (
                    <span className="plan-tier-current">Current plan</span>
                  ) : intent[p.id] ? (
                    <span className="plan-tier-thanks">
                      ✓ You're on the early-supporter list — we'll reach out when {p.name} opens.
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="plan-upgrade-btn"
                      onClick={() => handleUpgradeIntent(p.id)}
                    >
                      Upgrade to {p.name}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <p className="plan-honesty">
              Paid tiers are in early access. Clicking upgrade puts your workspace on the
              early-supporter list — you'll lock in early pricing when they launch, and nothing is
              charged today.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
